// src/features/executive/insight/widgets/RevenueByChannelBar.tsx
import { Box, Chip, Stack, Typography } from '@mui/material'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import CategoricalBar from './CategoricalBar'
import { formatCurrency, formatCurrencyCompact, formatVariance } from 'utils/executive.utils'
import { useRevenueByChannel } from '../hooks/use-revenue-by-channel'
import RevenueModeSelect from './RevenueModeSelect'
import type { RevenueMode, SourceChannelKey } from 'api/executive/executive.types'

const I18N: Record<SourceChannelKey, string> = {
  facebook: 'EXECUTIVE.INSIGHT.SOURCE_CHANNEL.FACEBOOK',
  referral: 'EXECUTIVE.INSIGHT.SOURCE_CHANNEL.REFERRAL',
  google: 'EXECUTIVE.INSIGHT.SOURCE_CHANNEL.GOOGLE',
  line: 'EXECUTIVE.INSIGHT.SOURCE_CHANNEL.LINE',
  walkin: 'EXECUTIVE.INSIGHT.SOURCE_CHANNEL.WALKIN',
  other: 'EXECUTIVE.INSIGHT.SOURCE_CHANNEL.OTHER',
}

// Same threshold as SourceChannelDonut (#7) so the data-quality signal stays
// consistent — but applied to revenue share, not patient share.
const UNKNOWN_WARNING_PCT = 25

// Marketing channels move fast in both directions (ad-spend cycles, organic
// drift). Surface non-'other' segment with the largest absolute baht variance.
const MOVER_MIN_VARIANCE = 0.2

function varianceOf(current: number, previous: number | null | undefined): number | null {
  if (previous == null || previous === 0) return null
  return (current - previous) / Math.abs(previous)
}

export default function RevenueByChannelBar() {
  const { t } = useTranslation()
  const [revenueMode, setRevenueMode] = useState<RevenueMode>('fee')
  const { data, loading, error, refetch } = useRevenueByChannel(revenueMode)

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const previousTotal = data?.previousTotal ?? null
  const unknownAmount = data?.unknownAmount ?? 0
  const totalVariance = varianceOf(total, previousTotal)

  // Data-quality nudge — NULL accessChannelId revenue folded into 'other'.
  const unknownPct = total > 0 ? (unknownAmount / total) * 100 : 0
  const showUnknownWarning = unknownPct > UNKNOWN_WARNING_PCT

  // Largest mover among non-'other' segments with previousAmount > 0.
  const mover = items
    .filter((i) => i.key !== 'other' && i.previousAmount != null && i.previousAmount > 0)
    .map((i) => ({ i, v: varianceOf(i.amount, i.previousAmount) }))
    .filter((x) => x.v != null && Math.abs(x.v as number) >= MOVER_MIN_VARIANCE)
    .sort((a, b) => Math.abs(b.v as number) - Math.abs(a.v as number))[0]

  const rows = items.map((i) => ({
    key: i.key,
    label: t(I18N[i.key]),
    count: i.amount,            // CategoricalBar's "value" — repurposed for baht
    percent: i.percent,
    previousCount: i.previousAmount,
  }))

  const subtitle = total > 0 && (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
      <span>{formatCurrencyCompact(total)}</span>
      {totalVariance != null && (
        <Typography
          component="span"
          variant="caption"
          sx={{ display: 'inline-flex', alignItems: 'center' }}
          color={totalVariance >= 0 ? 'success.main' : 'error.main'}
        >
          {totalVariance >= 0
            ? <TrendingUpIcon sx={{ fontSize: 13, mr: 0.25 }} />
            : <TrendingDownIcon sx={{ fontSize: 13, mr: 0.25 }} />}
          {formatVariance(totalVariance)}
        </Typography>
      )}
    </Box>
  )

  const notice = (showUnknownWarning || mover) ? (
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
      {showUnknownWarning && (
        <Chip
          icon={<WarningAmberOutlinedIcon />}
          label={
            <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
              {t(
                'EXECUTIVE.INSIGHT.REVENUE_BY_CHANNEL.UNSPECIFIED_WARNING',
                'ไม่ระบุช่องทาง {{pct}}% ของรายได้ — โปรดตรวจสอบการบันทึกข้อมูลในแบบฟอร์มลงทะเบียน',
                { pct: unknownPct.toFixed(0) }
              )}
            </Typography>
          }
          color="warning"
          variant="outlined"
          size="small"
          sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
        />
      )}
      {mover && (mover.v as number) >= 0 && (
        <Chip
          icon={<TrendingUpIcon />}
          label={
            <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
              {t(
                'EXECUTIVE.INSIGHT.REVENUE_BY_CHANNEL.MOVER_UP',
                'ช่องทางรายได้มาแรง: {{label}} ▲ {{pct}}',
                {
                  label: t(I18N[mover.i.key]),
                  pct: formatVariance(mover.v as number),
                }
              )}
            </Typography>
          }
          color="info"
          variant="outlined"
          size="small"
          sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
        />
      )}
      {mover && (mover.v as number) < 0 && (
        <Chip
          icon={<TrendingDownIcon />}
          label={
            <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
              {t(
                'EXECUTIVE.INSIGHT.REVENUE_BY_CHANNEL.MOVER_DOWN',
                'ช่องทางรายได้ลดลง: {{label}} ▼ {{pct}}',
                {
                  label: t(I18N[mover.i.key]),
                  pct: formatVariance(mover.v as number),
                }
              )}
            </Typography>
          }
          color="warning"
          variant="outlined"
          size="small"
          sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
        />
      )}
    </Stack>
  ) : null

  return (
    <CategoricalBar
      title={t('EXECUTIVE.INSIGHT.REVENUE_BY_CHANNEL.TITLE')}
      titleTooltip={t(
        'EXECUTIVE.INSIGHT.REVENUE_BY_CHANNEL.SOURCE_TOOLTIP',
        'อ้างอิงจาก SUM(orders.amountPaid) — เงินที่จ่ายแล้วจริง (ไม่ใช่ยอดในใบเสร็จ) ของ orders ที่ confirmed — attributed ไปยัง patients.accessChannelId\nใช้ keyword mapping เดียวกับ SourceChannelDonut: ดูกราฟ #7 เปรียบเทียบ "จำนวนคนไข้" กับ "รายได้" เพื่อหา channel ที่ได้คนไข้ high-value (รายได้สูง แต่ patient count ไม่สูง = best ROI)'
      )}
      subtitle={subtitle || undefined}
      headerAction={<RevenueModeSelect value={revenueMode} onChange={setRevenueMode} />}
      notice={notice}
      emptyMessage={t(
        'EXECUTIVE.INSIGHT.REVENUE_BY_CHANNEL.EMPTY',
        'ไม่พบรายได้จากช่องทางใดในช่วงเวลาและเงื่อนไขที่เลือก'
      )}
      rows={rows}
      barColor="#2a8d9c"
      formatValue={(r) => `${formatCurrency(r.count)} (${r.percent.toFixed(0)}%)`}
      loading={loading}
      error={error}
      onRetry={refetch}
    />
  )
}
